import { Prompt } from 'react-router-dom';
import React from 'react';
import PropTypes from 'prop-types';
import RaisedButton from 'material-ui/RaisedButton';
import MenuItem from 'material-ui/MenuItem';
import Snackbar from 'material-ui/Snackbar';
import { FormsySelect, FormsyText } from 'formsy-material-ui/lib';
import Formsy from 'formsy-react';
import { Typography, Box } from '@material-ui/core';
import moment from 'moment';
import { FormsyNHS } from '../../common/Formsy';
import PostcodeSearch from '../../../containers/common/PostcodeSearch/PostcodeSearchContainer';
import AddressForm from '../../Treatments/AddressDetails/AddressForm';
import { actionTypes } from '../../../settings/constants';
import { textFieldStyle, common, dropDownStyle } from '../../../theme/AllocateBaseTheme';
import globals from '../../../globals/communityGlobals';
import { doubleActionPrevent, refSetter } from '../../../utils/helpers';
import PatientPropType from '../../../propTypes/entities/patient';
import {
  convertDateFromServer,
  extractDateOnly,
  isDateFromBeforeDateTo,
  isValidPastOrCurrentDate,
} from './../../../utils/dates';
import { phoneNumber } from '../../../settings/constants/regex';
import KeyboardDateSelector from '../../common/KeyboardDateTimeSelectors/KeyboardDateSelector';
import { getErrorProps, checkRequiredOnBlur } from '../../../utils/keyboardSelectorFunctions';

const locale = globals.getArea('patients');
const localeTreatments = globals.getArea('treatments');
const errorsLocale = globals.getArea('error');

const TITLES = ['Mr', 'Mrs', 'Ms', 'Master', 'Miss', 'Doctor'];
const GENDERS = ['Male', 'Female', 'Other'];

class PatientForm extends React.PureComponent {
  constructor(props) {
    super(props);

    const { dateOfBirth, dateOfDeath } = this.props.patient || {};
    const birthDate = dateOfBirth ? new Date(convertDateFromServer(dateOfBirth)) : null;
    const deathDate = dateOfDeath ? new Date(convertDateFromServer(dateOfDeath)) : null;

    this.state = {
      canSubmit: false,
      unSavedChanges: false,
      hasPostCode: props.actionType === 'EDIT',
      open: false,
      newPatientPasKey: null,
      hasBeenEdited: props.actionType === 'CREATE',
      datesAreValidAndHaveChanged: false,
      patient: props.patient,
      dateOfBirth: birthDate,
      dateOfDeath: deathDate,
      validationErrors: {},
    };
  }

  componentWillMount() {
    // preventing navigation from the route
    window.onbeforeunload = () => this.state.unSavedChanges;
  }

  componentWillUnmount() {
    this.setState({
      unSavedChanges: false,
    });

    window.onbeforeunload = null;
  }

  onPostcodeChange = state => {
    this.setState({
      hasPostCode: state,
    });
  };

  setValidationErrors = newValidationErrors => {
    this.setState({
      validationErrors: newValidationErrors,
    });
  };

  setRef = refSetter.bind(this);

  enableButton = () => {
    this.setState({ canSubmit: true });
  };
  disableButton = () => {
    this.setState({ canSubmit: false });
  };
  handleFormChange = (values, isChanged) => {
    this.setState({
      unSavedChanges: true,
      hasBeenEdited: isChanged,
    });
  };

  handleRequestClose = () => this.setState({ open: false });

  formatDate = date => moment(date).format(globals.dateFormats.short);

  save = doubleActionPrevent.bind(this)(
    'Save',
    async model => {
      this.state.unSavedChanges = false;
      const { submitAction } = this.props;
      const patient = this.state.patient || {};
      const addressFormModel = this.addressForm.getModel();
      const isEditMode = this.props.actionType === 'EDIT';
      const updated = Object.assign({}, patient, {
        nhsNumber: model.nhsNumber,
        title: model.title ? model.title : null,
        forename: model.forename,
        surname: model.surname,
        gender: model.gender ? model.gender : null,
        pasNumber: model.pasNumber,
        dateOfBirth: moment(this.state.dateOfBirth).format(globals.dateFormats.date),
        dateOfDeath: this.state.dateOfDeath ? moment(this.state.dateOfDeath).format(globals.dateFormats.date) : null,
        gpPractice: model.gpPractice,
        importedByExternalProvider: patient.importedByExternalProvider,
        contact: {
          homeTelephone: model.homeTelephone.trim(),
          workTelephone: model.workTelephone.trim(),
          mobileTelephone: model.mobileTelephone.trim(),
          fax: patient.fax,
          bleeper: patient.bleeper,
          email1: patient.email1,
          email2: patient.email2,
          webAddress: patient.webAddress,
        },
        address: {
          addressLine1: addressFormModel.address.addressLine1,
          addressLine2: addressFormModel.address.addressLine2,
          addressLine3: addressFormModel.address.addressLine3,
          townCity: addressFormModel.address.townCity,
          postCode: addressFormModel.address.postCode,
          accessDetails: addressFormModel.address.accessDetails,
        },
        isActive: isEditMode ? patient.isActive : true,
        // eslint-disable-next-line no-dupe-keys
      });

      this.setState({ patient: updated });

      await submitAction({
        patient: updated,
        hasPostcodeChanged: isEditMode && patient.address.postCode !== addressFormModel.address.postCode,
      }).then(response => {
        this.setState({ newPatientPasKey: response.response.data.pasKey, hasBeenEdited: false });
        if (response.response.data.isActive != null) {
          this.props.updatePatientIsActiveStatus(response.response.data.isActive);
        }
      });
    },
    () => {
      this.setState({ open: true });
      if (this.props.actionType === actionTypes.CREATE) {
        this.props.navigate(`/Patients/${this.state.newPatientPasKey}`);
      }
    },
  );

  updateAddressFromPostcode = selection => {
    this.addressFields.setValue({
      addressLine1: selection.lines[0],
      addressLine2: selection.lines[1],
      addressLine3: selection.lines[2],
      townCity: selection.postTown,
      postCode: selection.value,
    });
    this.onPostcodeChange(selection.value !== '');
  };

  checkValidDateOfDeath = (dateOfDeath, dateOfBirth) => {
    return !dateOfDeath || (isValidPastOrCurrentDate(dateOfDeath) && isDateFromBeforeDateTo(dateOfBirth, dateOfDeath));
  };

  checkSomeDateChanged = (dateOfBirth, dateOfDeath) => {
    const patient = this.props.patient;
    return (
      (dateOfBirth && dateOfBirth.getTime() !== extractDateOnly(new Date(patient.dateOfBirth)).getTime()) ||
      (dateOfDeath && dateOfDeath.getTime() !== extractDateOnly(new Date(patient.dateOfDeath)).getTime()) ||
      (!dateOfDeath && patient.dateOfDeath)
    );
  };

  handleDateChange = (fieldName, date) => {
    const { validationErrors, dateOfBirth, dateOfDeath } = this.state;
    const { actionType } = this.props;
    const errorMsg =
      fieldName === 'dateOfBirth' && !date ? errorsLocale.getMsg('requiredFieldValidationMessage') : null;

    const updatedDateOfBirth = fieldName === 'dateOfBirth' ? date : dateOfBirth;
    const updatedDateOfDeath = fieldName === 'dateOfDeath' ? date : dateOfDeath;

    const datesAreValid =
      isValidPastOrCurrentDate(updatedDateOfBirth) &&
      this.checkValidDateOfDeath(updatedDateOfDeath, updatedDateOfBirth);
    const datesHaveChanged = actionType === 'EDIT' && this.checkSomeDateChanged(updatedDateOfBirth, updatedDateOfDeath);

    this.setState({
      validationErrors: { ...validationErrors, [fieldName]: errorMsg },
      [fieldName]: date,
      datesAreValidAndHaveChanged: datesAreValid && datesHaveChanged,
    });
  };

  isButtonDisabled = () => {
    const { canSubmit, hasPostCode, hasBeenEdited, dateOfBirth, dateOfDeath, datesAreValidAndHaveChanged } = this.state;

    return !(
      canSubmit &&
      ((hasPostCode &&
        hasBeenEdited &&
        isValidPastOrCurrentDate(dateOfBirth) &&
        this.checkValidDateOfDeath(dateOfDeath, dateOfBirth)) ||
        datesAreValidAndHaveChanged)
    );
  };

  render() {
    const { actionType, canEditPatients, enableIntegrationMode } = this.props;
    const patient = this.props.patient || {};
    const { address = {}, contact = {} } = patient;

    const errorMessages = {
      matchRegexp: errorsLocale.getMsg('invalidFormat'),
    };

    const isCreate = actionType === actionTypes.CREATE;
    const messageSuffix = isCreate ? 'created' : 'edited';

    return (
      <React.Fragment>
        <Snackbar
          open={this.state.open}
          message={`Patient ${messageSuffix}`}
          autoHideDuration={4000}
          onRequestClose={this.handleRequestClose}
        />
        <Formsy.Form
          onValidSubmit={this.submit}
          onValid={this.enableButton}
          onInvalid={this.disableButton}
          onSubmit={this.save}
          onChange={this.handleFormChange}
          className="tab-box"
        >
          <Prompt when={this.state.unSavedChanges} message={`${locale.getMsg('messagePopUp')}`} />
          <Box display="flex" flexDirection="column" p={1}>
            <Box display="flex" justifyContent="space-between">
              <Box p={1}>
                <Typography variant="h5">{locale.getMsg('patientDetails')}</Typography>
              </Box>
              <Box>
                {canEditPatients && (
                  <div style={{ justifyContent: 'flex-end' }}>
                    <RaisedButton
                      primary
                      label={locale.getMsg(`${isCreate ? 'create' : 'save'}SubmitLabel`)}
                      type="submit"
                      disabled={this.isButtonDisabled()}
                      style={{ width: '150px', textAlign: 'right', float: 'right' }}
                    />
                  </div>
                )}
              </Box>
            </Box>

            <Box>
              <div className="row">
                <Box display="flex">
                  <Box p={1} flexGrow={1} style={{ width: '33.3%' }}>
                    <FormsyText
                      name="forename"
                      floatingLabelStyle={textFieldStyle.floatingLabelStyle}
                      floatingLabelShrinkStyle={textFieldStyle.floatingLabelShrinkStyle}
                      style={textFieldStyle.style}
                      inputStyle={textFieldStyle.inputStyle}
                      underlineStyle={textFieldStyle.underlineStyle}
                      floatingLabelText={`${locale.getMsg('firstNames')} *`}
                      value={patient.forename || ''}
                      required
                      disabled={!canEditPatients || enableIntegrationMode}
                    />
                  </Box>
                  <Box p={1} flexGrow={1} style={{ width: '33.3%' }}>
                    <FormsyText
                      name="surname"
                      floatingLabelStyle={textFieldStyle.floatingLabelStyle}
                      floatingLabelShrinkStyle={textFieldStyle.floatingLabelShrinkStyle}
                      style={textFieldStyle.style}
                      inputStyle={textFieldStyle.inputStyle}
                      underlineStyle={textFieldStyle.underlineStyle}
                      floatingLabelText={`${locale.getMsg('lastName')} *`}
                      value={patient.surname || ''}
                      required
                      disabled={!canEditPatients || enableIntegrationMode}
                    />
                  </Box>
                  <Box p={1} flexGrow={1} style={{ width: '33.3%' }}>
                    <FormsyNHS
                      name="nhsNumber"
                      floatingLabelText={`${locale.getMsg('nhsNumber')} *`}
                      className="formsytext-css patient-error-msg"
                      floatingLabelStyle={textFieldStyle.floatingLabelStyle}
                      floatingLabelShrinkStyle={textFieldStyle.floatingLabelShrinkStyle}
                      floatingLabelFocusStyle={textFieldStyle.floatingLabelFocusStyle}
                      style={textFieldStyle.style}
                      inputStyle={textFieldStyle.inputStyle}
                      underlineStyle={textFieldStyle.underlineStyle}
                      underlineFocusStyle={textFieldStyle.underlineFocusStyle}
                      validationColor={textFieldStyle.validationColor}
                      value={patient.nhsNumber || ''}
                      required
                      disabled={!canEditPatients || enableIntegrationMode}
                    />
                  </Box>
                </Box>

                <Box display="flex">
                  <Box p={1} flexGrow={1} style={{ width: '33.3%' }}>
                    <FormsySelect
                      name="title"
                      floatingLabelText={locale.getMsg('title')}
                      value={patient.title || ''}
                      floatingLabelStyle={common.floatingLabelStyle}
                      floatingLabelShrinkStyle={common.floatingLabelShrinkStyle}
                      floatingLabelFocusStyle={common.floatingLabelFocusStyle}
                      underlineStyle={common.underlineStyle}
                      style={dropDownStyle.style}
                      iconStyle={dropDownStyle.iconStyle}
                      labelStyle={dropDownStyle.labelStyle}
                      menuStyle={dropDownStyle.menuStyle}
                      disabled={!canEditPatients || enableIntegrationMode}
                    >
                      {TITLES.map(item => (
                        <MenuItem key={`title_${item}`} value={item} primaryText={locale.getMsg(item.toLowerCase())} />
                      ))}
                    </FormsySelect>
                  </Box>
                  <Box p={1} flexGrow={1} style={{ width: '33.3%' }}>
                    <FormsySelect
                      name="gender"
                      floatingLabelText={locale.getMsg('gender')}
                      value={patient.gender || ''}
                      floatingLabelStyle={common.floatingLabelStyle}
                      floatingLabelShrinkStyle={common.floatingLabelShrinkStyle}
                      floatingLabelFocusStyle={common.floatingLabelFocusStyle}
                      underlineStyle={common.underlineStyle}
                      style={dropDownStyle.style}
                      iconStyle={dropDownStyle.iconStyle}
                      labelStyle={dropDownStyle.labelStyle}
                      menuStyle={dropDownStyle.menuStyle}
                      disabled={!canEditPatients || enableIntegrationMode}
                    >
                      {GENDERS.map(item => (
                        <MenuItem key={`gender_${item}`} value={item} primaryText={locale.getMsg(item.toLowerCase())} />
                      ))}
                    </FormsySelect>
                  </Box>
                  <Box p={1} flexGrow={1} style={{ width: '33.3%' }}>
                    <KeyboardDateSelector
                      name="dateOfBirth"
                      className="mui-date-picker"
                      required
                      label={locale.getMsg('dateOfBirth')}
                      value={this.state.dateOfBirth}
                      onChange={date => this.handleDateChange('dateOfBirth', date)}
                      onBlur={event =>
                        checkRequiredOnBlur(
                          event.target.value,
                          this.setValidationErrors,
                          'dateOfBirth',
                          this.state.validationErrors,
                        )
                      }
                      disabled={!canEditPatients || enableIntegrationMode}
                      errorProps={getErrorProps(this.state.validationErrors.dateOfBirth)}
                      openTo="year"
                      maxDate={new Date(globals.now)}
                      maxDateMessage={errorsLocale.getMsg('cannotBeInTheFuture')}
                    />
                  </Box>
                </Box>
              </div>
            </Box>
            <Box>
              <div className="row">
                <Box p={1}>
                  <Typography variant="h5">{locale.getMsg('contactDetails')}</Typography>
                </Box>
                <Box display="flex">
                  <Box p={1} flexGrow={1} style={{ width: '25%' }}>
                    <FormsyText
                      name="homeTelephone"
                      validations={!enableIntegrationMode ? { matchRegexp: phoneNumber } : {}}
                      validationErrors={errorMessages}
                      floatingLabelStyle={textFieldStyle.floatingLabelStyle}
                      floatingLabelShrinkStyle={textFieldStyle.floatingLabelShrinkStyle}
                      style={textFieldStyle.style}
                      inputStyle={textFieldStyle.inputStyle}
                      underlineStyle={textFieldStyle.underlineStyle}
                      floatingLabelText={locale.getMsg('landlinePhoneNumber')}
                      value={contact.homeTelephoneDisplay || ''}
                      validationColor={textFieldStyle.validationColor}
                      disabled={!canEditPatients || enableIntegrationMode}
                      errorStyle={textFieldStyle.errorStyle}
                    />
                  </Box>
                  <Box p={1} flexGrow={1} style={{ width: '25%' }}>
                    <FormsyText
                      name="workTelephone"
                      validations={!enableIntegrationMode ? { matchRegexp: phoneNumber } : {}}
                      validationErrors={errorMessages}
                      floatingLabelStyle={textFieldStyle.floatingLabelStyle}
                      floatingLabelShrinkStyle={textFieldStyle.floatingLabelShrinkStyle}
                      style={textFieldStyle.style}
                      inputStyle={textFieldStyle.inputStyle}
                      underlineStyle={textFieldStyle.underlineStyle}
                      floatingLabelText={locale.getMsg('otherPhoneNumber')}
                      value={contact.workTelephoneDisplay || ''}
                      validationColor={textFieldStyle.validationColor}
                      disabled={!canEditPatients || enableIntegrationMode}
                      errorStyle={textFieldStyle.errorStyle}
                    />
                  </Box>
                  <Box p={1} flexGrow={1} style={{ width: '25%' }}>
                    <FormsyText
                      name="mobileTelephone"
                      validations={!enableIntegrationMode ? { matchRegexp: phoneNumber } : {}}
                      validationErrors={errorMessages}
                      floatingLabelStyle={textFieldStyle.floatingLabelStyle}
                      floatingLabelShrinkStyle={textFieldStyle.floatingLabelShrinkStyle}
                      style={textFieldStyle.style}
                      inputStyle={textFieldStyle.inputStyle}
                      underlineStyle={textFieldStyle.underlineStyle}
                      floatingLabelText={locale.getMsg('mobilePhoneNumber')}
                      validationColor={textFieldStyle.validationColor}
                      value={contact.mobileTelephoneDisplay || ''}
                      disabled={!canEditPatients || enableIntegrationMode}
                      errorStyle={textFieldStyle.errorStyle}
                    />
                  </Box>
                  <Box p={1} flexGrow={1} style={{ width: '25%' }}>
                    <KeyboardDateSelector
                      name="dateOfDeath"
                      className="mui-date-picker"
                      label={locale.getMsg('dateOfDeath')}
                      value={this.state.dateOfDeath}
                      onChange={date => this.handleDateChange('dateOfDeath', date)}
                      disabled={!canEditPatients || enableIntegrationMode}
                      errorProps={
                        !this.state.dateOfDeath
                          ? {
                              helperText: null,
                              error: false,
                            }
                          : getErrorProps(this.state.validationErrors.dateOfDeath)
                      }
                      minDate={this.state.dateOfBirth}
                      minDateMessage={localeTreatments.getMsg('cannotBeEarlier', [locale.getMsg('dateOfBirth')])}
                      maxDate={new Date(globals.now)}
                      maxDateMessage={errorsLocale.getMsg('cannotBeInTheFuture')}
                    />
                  </Box>
                </Box>
                <Box display="flex">
                  <Box p={1} flexGrow={1} style={{ width: '25%' }}>
                    <FormsyText
                      className="col-c4"
                      name="gpPractice"
                      floatingLabelStyle={textFieldStyle.floatingLabelStyle}
                      floatingLabelShrinkStyle={textFieldStyle.floatingLabelShrinkStyle}
                      style={textFieldStyle.style}
                      inputStyle={textFieldStyle.inputStyle}
                      underlineStyle={textFieldStyle.underlineStyle}
                      floatingLabelText={locale.getMsg('gpPracticeName')}
                      value={patient.gpPractice || ''}
                      disabled={!canEditPatients || enableIntegrationMode}
                    />
                  </Box>
                  <Box p={1} flexGrow={1} style={{ width: '25%' }}>
                    <FormsyText
                      className="col-c4"
                      name="pasId"
                      floatingLabelStyle={textFieldStyle.floatingLabelStyle}
                      floatingLabelShrinkStyle={textFieldStyle.floatingLabelShrinkStyle}
                      style={textFieldStyle.style}
                      inputStyle={textFieldStyle.inputStyle}
                      underlineStyle={textFieldStyle.underlineStyle}
                      floatingLabelText={locale.getMsg('pasKey')}
                      value={patient ? patient.id : ''}
                      disabled
                    />
                  </Box>
                  <Box p={1} flexGrow={1} style={{ width: '25%' }}>
                    <FormsyText
                      className="col-c4"
                      name="pasNumber"
                      floatingLabelStyle={textFieldStyle.floatingLabelStyle}
                      floatingLabelShrinkStyle={textFieldStyle.floatingLabelShrinkStyle}
                      style={textFieldStyle.style}
                      inputStyle={textFieldStyle.inputStyle}
                      underlineStyle={textFieldStyle.underlineStyle}
                      floatingLabelText={locale.getMsg('pasNumber')}
                      value={patient ? patient.pasNumber : ''}
                      disabled={!canEditPatients || enableIntegrationMode}
                    />
                  </Box>
                  <Box p={1} flexGrow={1} style={{ width: '25%' }} />
                </Box>
              </div>
            </Box>
            <Box>
              <div className="row">
                <Box p={1}>
                  <Typography variant="h5">{locale.getMsg('patientAddress')}</Typography>
                </Box>

                <Formsy.Form ref={this.setRef('addressForm')} onChange={this.handleFormChange}>
                  {!enableIntegrationMode && (
                    <Box display="flex">
                      <Box p={1} flexGrow={1} style={{ width: '25%' }}>
                        <PostcodeSearch onSelect={this.updateAddressFromPostcode} />
                      </Box>
                    </Box>
                  )}
                  <AddressForm
                    ref={this.setRef('addressFields')}
                    value={address}
                    name="address"
                    onPostcodeChange={state => this.onPostcodeChange(state)}
                    disabled={!canEditPatients || enableIntegrationMode}
                    accessDetailsDisabled={false}
                  />
                </Formsy.Form>
              </div>
            </Box>
          </Box>
        </Formsy.Form>
      </React.Fragment>
    );
  }
}

PatientForm.propTypes = {
  patient: PatientPropType,
  navigate: PropTypes.func.isRequired,
  actionType: PropTypes.string,
  canEditPatients: PropTypes.bool,
  enableIntegrationMode: PropTypes.bool.isRequired,
};

PatientForm.defaultProps = {
  patient: null,
  navigate: null,
  actionType: 'EDIT',
  canEditPatients: true,
  enableIntegrationMode: false,
};

export default PatientForm;
